import { useEffect, useMemo, useRef, useState } from "react";
import {
  Users,
  Boxes,
  FolderCheck,
  TrendingUp,
  AlertTriangle,
  Clock,
  Package,
  CheckCircle,
  Zap,
  Plus,
  User,
  Users as UsersIcon,
  RotateCw,
  ChevronDown,
} from "lucide-react";
import {
  Chart as ChartJS,
  ArcElement,
  LineElement,
  BarElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Filler,
  Tooltip,
  Legend,
} from "chart.js";
import { Doughnut, Line, Bar } from "react-chartjs-2";
import CompanyAdminLayout from "../../layouts/CompanyAdminLayout";
import BoxLinksPanel from "../../components/common/BoxLinksPanel";
import "../../assets/styles/colors.css";
import { useAuth } from "../../context/AuthContext";
import {
  getOrganizationOverview,
  normalizeOrganizationOverview,
  getMemberAnalytics,
  normalizeMemberAnalytics,
  getPerformanceMetrics,
  normalizePerformanceMetrics,
  getLearningActivity,
  normalizeLearningActivity,
  getBoxLibrary,
  normalizeBoxLibrary,
  getTokenUsage,
  normalizeTokenUsage,
} from "../../api/services/companyAdminDashboardService";

ChartJS.register(
  ArcElement,
  LineElement,
  BarElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Filler,
  Tooltip,
  Legend,
);

/* ─────────────────────────────────────────────
   Theme bridge — Chart.js needs concrete colors, not CSS vars, so we read
   ───────────────────────────────────────────── */
function useThemeColors() {
  const [c, setC] = useState(null);
  useEffect(() => {
    const readColors = () => {
      const root = getComputedStyle(document.documentElement);
      const v = (name, fallback) =>
        (root.getPropertyValue(name) || "").trim() || fallback;
      setC({
        primary: v("--color-primary", "#00e639"),
        primaryHover: v("--color-primary-hover", "#00c430"),
        accentViolet: v("--color-accent-violet", "#8b5cf6"),
        textPrimary: v("--color-text-primary", "#dfe2eb"),
        textSecondary: v("--color-text-secondary", "#8fa3be"),
        textMuted: v("--color-text-muted", "#3e4f65"),
        border: v("--color-border", "#212a38"),
        surface: v("--color-surface", "#181c22"),
        blue: "#3b82f6",
        violet: "#8b5cf6",
        orange: "#f59e0b",
        teal: "#0d9488",
        yellow: "#ca8a04",
        red: "#ef4444",
      });
    };

    readColors();
    const observer = new MutationObserver(readColors);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
  }, []);
  return c;
}

function withAlpha(hex, a) {
  const m = hex.replace("#", "");
  const n =
    m.length === 3
      ? m
          .split("")
          .map((x) => x + x)
          .join("")
      : m;
  const r = parseInt(n.slice(0, 2), 16);
  const g = parseInt(n.slice(2, 4), 16);
  const b = parseInt(n.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${a})`;
}

function useCountUp(target, run, dur = 1300) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!run) return;
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / dur, 1);
      const e = 1 - Math.pow(1 - p, 3);
      setVal(target * e);
      if (p < 1) raf = requestAnimationFrame(tick);
      else setVal(target);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, run, dur]);
  return Math.round(val).toLocaleString();
}

function useInView(threshold = 0.1) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setInView(true);
            obs.unobserve(e.target);
          }
        });
      },
      { threshold },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

const TINT_HEX = {
  primary: "var(--color-primary)",
  blue: "#3b82f6",
  violet: "#8b5cf6",
  orange: "#f59e0b",
  teal: "#0d9488",
  red: "#ef4444",
};

/* ── Building blocks ─────────────────────────── */
function SectionLabel({ children }) {
  return (
    <div className="text-[10.5px] tracking-[0.22em] uppercase font-semibold text-(--color-text-muted) font-mono">
      {children}
    </div>
  );
}

function Card({ children, className = "", style, ...rest }) {
  return (
    <div className={`qv-card qv-noise ${className}`} style={style} {...rest}>
      {children}
    </div>
  );
}

function Avatar({ initials, tint }) {
  const hex = TINT_HEX[tint];
  const isPrimary = tint === "primary";
  return (
    <span
      className="w-7 h-7 rounded-full inline-flex items-center justify-center text-[11px] font-bold shrink-0"
      style={{
        background: isPrimary
          ? "color-mix(in srgb, var(--color-primary) 15%, transparent)"
          : withAlpha(hex, 0.15),
        color: hex,
      }}
    >
      {initials}
    </span>
  );
}

function tipPreset(colors) {
  return {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderWidth: 1,
    titleColor: colors.textPrimary,
    bodyColor: colors.textSecondary,
    padding: 10,
    cornerRadius: 8,
    displayColors: true,
    boxWidth: 8,
    boxHeight: 8,
    boxPadding: 4,
  };
}

/* Hover-gated glow for line charts — glows the whole line while the mouse
   is over the canvas. Mirrors the Platform Admin dashboard. */
const lineGlowPlugin = {
  id: "lineGlow",
  afterDatasetsDraw(chart) {
    if (!chart._lineGlowHover) return;
    const { ctx } = chart;
    chart.data.datasets.forEach((dataset, i) => {
      const meta = chart.getDatasetMeta(i);
      if (meta.hidden || meta.type !== "line" || !meta.dataset) return;
      [8, 18].forEach((blur) => {
        ctx.save();
        ctx.shadowColor = dataset.borderColor;
        ctx.shadowBlur = blur;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 0;
        meta.dataset.draw(ctx, chart.chartArea);
        ctx.restore();
      });
    });
  },
  afterEvent(chart, args) {
    const was = chart._lineGlowHover;
    const now = args.event.type !== "mouseout";
    if (was !== now) {
      chart._lineGlowHover = now;
      chart.draw();
    }
  },
};

/* Glow plugin for bar charts — glows the hovered bar */
const barGlowPlugin = {
  id: "barGlow",
  afterDatasetsDraw(chart) {
    const active = chart.getActiveElements();
    if (!active.length) return;
    const { ctx } = chart;
    active.forEach(({ element, datasetIndex, index }) => {
      const ds = chart.data.datasets[datasetIndex];
      const color = Array.isArray(ds.backgroundColor)
        ? ds.backgroundColor[index]
        : ds.backgroundColor;
      ctx.save();
      ctx.shadowColor = color;
      ctx.shadowBlur = 22;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 0;
      element.draw(ctx);
      ctx.restore();
    });
  },
};

/* Pretty, actionable error state shown inside any section's Card */
function SectionError({ message, onRetry }) {
  return (
    <div className="flex flex-col items-center justify-center text-center gap-3 px-5 py-9">
      <div className="w-11 h-11 rounded-full flex items-center justify-center bg-red-500/10 border border-red-500/20">
        <AlertTriangle size={20} className="text-red-400" />
      </div>
      <div>
        <div className="text-[13px] font-semibold text-(--color-text-primary)">
          Couldn’t load this section
        </div>
        <div className="text-[12px] text-(--color-text-muted) mt-0.5">
          {message || "Something went wrong fetching the data."}
        </div>
      </div>
      {onRetry && (
        <button
          onClick={onRetry}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-semibold text-(--color-primary) bg-(--color-primary)/8 border border-(--color-primary)/20 hover:bg-(--color-primary)/14 hover:-translate-y-px transition-all"
        >
          <RotateCw size={13} />
          Try again
        </button>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   Section: Organization Overview (3 metric cards)
   ───────────────────────────────────────────── */
const DECOR_TEAM_BARS = [46, 70, 38, 88, 60, 100, 54];

function OrgOverview({ colors }) {
  const [ref, inView] = useInView();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reload, setReload] = useState(0);
  const retry = () => setReload((n) => n + 1);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const raw = await getOrganizationOverview();
        if (!cancelled) setData(normalizeOrganizationOverview(raw));
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reload]);

  const run = inView && !!data;
  const members = useCountUp(data?.totalMembers ?? 0, run);
  const teams = useCountUp(data?.totalTeams ?? 0, run);
  const owned = useCountUp(data?.ownedBoxes ?? 0, run);

  // Build a normalized SVG sparkline path from the 30-pt growth series.
  const sparkPath = useMemo(() => {
    const series = data?.memberGrowth ?? [];
    if (series.length === 0) return "M0,44 L120,44";
    const W = 120;
    const H = 48;
    const pad = 4;
    const max = Math.max(...series, 1);
    const step = series.length > 1 ? W / (series.length - 1) : 0;
    return series
      .map((v, i) => {
        const x = +(i * step).toFixed(2);
        const y = +(H - pad - (v / max) * (H - pad * 2)).toFixed(2);
        return `${i === 0 ? "M" : "L"}${x},${y}`;
      })
      .join(" ");
  }, [data]);

  // Approval ring geometry
  const approvedPct = data?.approval.approvedPct ?? 0;
  const r = 29;
  const circ = 2 * Math.PI * r;
  const dash = (approvedPct / 100) * circ;

  if (error) {
    return (
      <section ref={ref}>
        <div className="mb-4">
          <SectionLabel>Organization Overview</SectionLabel>
        </div>
        <Card>
          <SectionError message={error} onRetry={retry} />
        </Card>
      </section>
    );
  }

  if (loading || !data) {
    return (
      <section ref={ref}>
        <div className="mb-4">
          <SectionLabel>Organization Overview</SectionLabel>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4.5">
          {[0, 1, 2].map((i) => (
            <Card key={i} className="p-5">
              <div className="animate-pulse flex flex-col gap-3">
                <div className="flex items-center gap-2.75">
                  <div className="w-10 h-10 rounded-full bg-white/5" />
                  <div className="h-3 w-32 rounded bg-white/5" />
                </div>
                <div className="h-16 rounded bg-white/5" />
              </div>
            </Card>
          ))}
        </div>
      </section>
    );
  }

  return (
    <section ref={ref}>
      <div className="mb-4">
        <SectionLabel>Organization Overview</SectionLabel>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4.5">
        {/* Total Members */}
        <Card
          className="p-5 qv-ping"
          style={{ "--ping-x": "50%", "--ping-y": "50%" }}
        >
          {" "}
          <div className="flex items-center gap-2.75 mb-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center bg-(--color-primary)/15">
              <Users size={20} className="text-(--color-primary)" />
            </div>
            <div className="flex flex-col">
              <span className="text-[13px] text-(--color-text-primary)">
                Total Members
              </span>
              <span className="text-[11px] text-(--color-text-muted)">
                Active across organization
              </span>
            </div>
          </div>
          <div className="flex items-end justify-between gap-3">
            <div>
              <div className="qv-num text-[38px] leading-none text-(--color-text-primary)">
                {members}
              </div>
              <div className="flex items-center gap-1 text-(--color-primary) text-[12.5px] mt-1.5">
                <TrendingUp size={14} />+{data.newMembers30d} this month
              </div>
            </div>
            <svg
              viewBox="0 0 120 48"
              preserveAspectRatio="none"
              className="w-30 h-12 transition-[filter] duration-300 hover:[filter:drop-shadow(0_0_4px_var(--color-primary))_drop-shadow(0_0_10px_var(--color-primary))]"
            >
              <defs>
                <linearGradient id="ca-sg" x1="0" y1="0" x2="0" y2="1">
                  <stop
                    offset="0%"
                    stopColor={withAlpha(colors.primary, 0.28)}
                  />
                  <stop
                    offset="100%"
                    stopColor={withAlpha(colors.primary, 0)}
                  />
                </linearGradient>
              </defs>
              <path d={`${sparkPath} L120,48 L0,48 Z`} fill="url(#ca-sg)" />
              <path
                d={sparkPath}
                fill="none"
                stroke={colors.primary}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </Card>

        {/* Total Teams */}
        <Card
          className="p-5 qv-ping"
          style={{ "--ping-x": "50%", "--ping-y": "50%" }}
        >
          {" "}
          <div className="flex items-center gap-2.75 mb-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center bg-blue-500/12">
              <UsersIcon size={20} className="text-blue-400" />
            </div>
            <div className="flex flex-col">
              <span className="text-[13px] text-(--color-text-primary)">
                Total Teams
              </span>
              <span className="text-[11px] text-(--color-text-muted)">
                Across all departments
              </span>
            </div>
          </div>
          <div className="flex items-end justify-between gap-3">
            <div>
              <div className="qv-num text-[38px] leading-none text-(--color-text-primary)">
                {teams}
              </div>
              <div className="flex items-center gap-1 text-orange-500 text-[12.5px] mt-1.5">
                <AlertTriangle size={14} />
                {data.emptyTeams}{" "}
                {data.emptyTeams === 1 ? "empty team" : "empty teams"}
              </div>
            </div>
            <div className="flex items-end gap-0.75 h-12 transition-[filter] duration-300 hover:[filter:drop-shadow(0_0_4px_#3b82f6)_drop-shadow(0_0_10px_#3b82f6)]">
              {DECOR_TEAM_BARS.map((h, i) => (
                <span
                  key={i}
                  className="w-1.5 rounded-xs bg-blue-500/70 transition-all duration-700"
                  style={{ height: run ? `${(h / 100) * 48}px` : 0 }}
                />
              ))}
            </div>
          </div>
        </Card>

        {/* Owned Boxes */}
        <Card
          className="p-5 qv-ping"
          style={{ "--ping-x": "50%", "--ping-y": "50%" }}
        >
          {" "}
          <div className="flex items-center gap-2.75 mb-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center bg-violet-500/12">
              <FolderCheck size={20} className="text-violet-400" />
            </div>
            <div className="flex flex-col">
              <span className="text-[13px] text-(--color-text-primary)">
                Owned Boxes
              </span>
              <span className="text-[11px] text-(--color-text-muted)">
                Published &amp; in review
              </span>
            </div>
          </div>
          <div className="flex items-end justify-between gap-3">
            <div>
              <div className="qv-num text-[38px] leading-none text-(--color-text-primary)">
                {owned}
              </div>
              <div className="flex items-center gap-1 text-orange-500 text-[12.5px] mt-1.5">
                <Clock size={14} />
                {data.pendingApprovals} pending approval
              </div>
            </div>
            <div className="relative w-18 h-18 shrink-0 transition-[filter] duration-300 hover:[filter:drop-shadow(0_0_4px_#8b5cf6)_drop-shadow(0_0_10px_#8b5cf6)]">
              <svg viewBox="0 0 72 72" className="w-18 h-18">
                <circle
                  cx="36"
                  cy="36"
                  r={r}
                  fill="none"
                  stroke={withAlpha(colors.textPrimary, 0.07)}
                  strokeWidth="5"
                />
                <circle
                  cx="36"
                  cy="36"
                  r={r}
                  fill="none"
                  stroke={colors.violet}
                  strokeWidth="5"
                  strokeLinecap="round"
                  transform="rotate(-90 36 36)"
                  strokeDasharray={`${run ? dash : 0} ${circ}`}
                  style={{
                    transition:
                      "stroke-dasharray 1.2s cubic-bezier(.22,1,.36,1)",
                  }}
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <span className="qv-num text-[16px] text-(--color-text-primary)">
                  {approvedPct}%
                </span>
                <span className="text-[7px] tracking-[1px] text-(--color-text-muted) font-mono">
                  APPROVED
                </span>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   Section: Member Analytics (growth line + role donut)
   Wired to /api/v1/dashboard/company-admin/member-analytics
   ───────────────────────────────────────────── */
function MemberAnalytics({ colors }) {
  const [ref, inView] = useInView(0.05);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reload, setReload] = useState(0);
  const retry = () => setReload((n) => n + 1);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const raw = await getMemberAnalytics();
        if (!cancelled) setData(normalizeMemberAnalytics(raw));
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reload]);

  const run = inView && !!data;
  const total = useCountUp(
    (data?.roles.users ?? 0) + (data?.roles.admins ?? 0),
    run,
  );
  const users = useCountUp(data?.roles.users ?? 0, run);
  const admins = useCountUp(data?.roles.admins ?? 0, run);
  const [donutHover, setDonutHover] = useState(false);

  const growthData = useMemo(() => {
    if (!data) return null;
    return {
      labels: data.labels,
      datasets: [
        {
          data: data.series,
          fill: true,
          borderColor: colors.primary,
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 4,
          pointHoverBackgroundColor: colors.primary,
          tension: 0.4,
          backgroundColor: (ctx) => {
            const { ctx: c, chartArea } = ctx.chart;
            if (!chartArea) return withAlpha(colors.primary, 0.1);
            const g = c.createLinearGradient(0, 0, 0, 195);
            g.addColorStop(0, withAlpha(colors.primary, 0.2));
            g.addColorStop(1, withAlpha(colors.primary, 0));
            return g;
          },
        },
      ],
    };
  }, [data, colors]);

  const growthOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: {
        ...tipPreset(colors),
        callbacks: {
          label: (ctx) =>
            ` +${ctx.parsed.y} new ${ctx.parsed.y === 1 ? "member" : "members"}`,
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { maxTicksLimit: 7, color: colors.textMuted },
      },
      y: {
        beginAtZero: true,
        grid: { color: withAlpha(colors.textPrimary, 0.04) },
        ticks: { color: colors.textMuted, precision: 0 },
      },
    },
  };

  const roleData = useMemo(() => {
    if (!data) return null;
    return {
      labels: ["Company Users", "Company Admins"],
      datasets: [
        {
          data: [data.roles.users, data.roles.admins],
          backgroundColor: [colors.primary, colors.blue],
          borderWidth: 0,
          hoverBorderWidth: 0,
          borderRadius: 4,
          hoverOffset: 6,
        },
      ],
    };
  }, [data, colors]);

  const roleOptions = {
    cutout: "72%",
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        ...tipPreset(colors),
        callbacks: {
          label: (ctx) => ` ${ctx.label}: ${ctx.parsed.toLocaleString()}`,
        },
      },
    },
  };

  if (error) {
    return (
      <section ref={ref}>
        <div className="mb-4">
          <SectionLabel>Member Analytics</SectionLabel>
        </div>
        <Card>
          <SectionError message={error} onRetry={retry} />
        </Card>
      </section>
    );
  }

  const ready = !loading && !!data;

  return (
    <section ref={ref}>
      <div className="mb-4">
        <SectionLabel>Member Analytics</SectionLabel>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Member growth */}
        <Card className="p-5.5 lg:col-span-2">
          <div className="flex justify-between items-start mb-1.5">
            <div>
              <h3 className="text-[13.5px] font-semibold text-(--color-text-primary)">
                New Members — 7 Day Trend
              </h3>
              <p className="text-xs text-(--color-text-muted) mt-0.5">
                New members joining your organization each day
              </p>
            </div>
            <div className="text-right">
              <div className="qv-num text-[20px] text-(--color-primary)">
                {ready ? `+${data.newMembers7dTotal}` : "—"}
              </div>
              <div className="text-[10.5px] text-(--color-text-muted) font-mono">
                LAST 7D
              </div>
            </div>
          </div>
          <div className="h-48.75 mt-2.5">
            {ready ? (
              <Line
                data={growthData}
                options={growthOptions}
                plugins={[lineGlowPlugin]}
              />
            ) : (
              <div className="h-full w-full rounded-lg bg-white/5 animate-pulse" />
            )}
          </div>
        </Card>

        {/* Role distribution */}
        <Card
          className="p-5 qv-ping"
          style={{ "--ping-x": "50%", "--ping-y": "50%" }}
        >
          {" "}
          <h3 className="text-[13.5px] font-semibold mb-1 text-(--color-text-primary)">
            Members by Role
          </h3>
          <p className="text-xs text-(--color-text-muted) mb-3.5">
            Breakdown of {ready ? total : "—"} members
          </p>
          <div
            className="h-37.5 transition-[filter] duration-300"
            onMouseEnter={() => setDonutHover(true)}
            onMouseLeave={() => setDonutHover(false)}
            style={{
              filter: donutHover
                ? `drop-shadow(0 0 8px ${colors.primary}) drop-shadow(0 0 18px ${withAlpha(colors.primary, 0.35)})`
                : "none",
            }}
          >
            {ready ? (
              <Doughnut data={roleData} options={roleOptions} />
            ) : (
              <div className="h-full w-full rounded-full bg-white/5 animate-pulse mx-auto aspect-square" />
            )}
          </div>
          <div className="flex flex-col gap-2 mt-3.5">
            <div className="flex items-center justify-between text-[12.5px]">
              <span className="flex items-center gap-1.5 text-(--color-text-secondary)">
                <span className="w-2 h-2 rounded-full bg-(--color-primary) shrink-0" />
                Company Users
              </span>
              <span className="font-semibold text-(--color-text-primary)">
                {ready ? users : "—"}
              </span>
            </div>
            <div className="flex items-center justify-between text-[12.5px]">
              <span className="flex items-center gap-1.5 text-(--color-text-secondary)">
                <span className="w-2 h-2 rounded-full bg-blue-500 shrink-0" />
                Company Admins
              </span>
              <span className="font-semibold text-(--color-text-primary)">
                {ready ? admins : "—"}
              </span>
            </div>
          </div>
        </Card>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   Section: Performance metrics (top teams bar + performers table)
   Wired to /api/v1/dashboard/company-admin/performance-metrics
   ───────────────────────────────────────────── */
function PerformanceMetrics({ colors }) {
  const [ref] = useInView(0.05);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reload, setReload] = useState(0);
  const retry = () => setReload((n) => n + 1);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const raw = await getPerformanceMetrics(5, 5);
        if (!cancelled) setData(normalizePerformanceMetrics(raw));
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reload]);

  const teamsData = useMemo(() => {
    if (!data) return null;
    const basePalette = [
      withAlpha(colors.primary, 0.7),
      withAlpha(colors.primary, 0.55),
      withAlpha(colors.blue, 0.6),
      withAlpha(colors.violet, 0.6),
      withAlpha(colors.blue, 0.45),
      withAlpha(colors.teal, 0.6),
    ];
    return {
      labels: data.topTeams.labels,
      datasets: [
        {
          data: data.topTeams.values,
          backgroundColor: data.topTeams.values.map(
            (_, i) => basePalette[i % basePalette.length],
          ),
          borderRadius: 5,
          borderSkipped: false,
        },
      ],
    };
  }, [data, colors]);

  const teamsOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        ...tipPreset(colors),
        callbacks: { label: (ctx) => ` ${ctx.parsed.y} plays` },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: colors.textSecondary, font: { size: 11 } },
      },
      y: {
        beginAtZero: true,
        grid: { color: withAlpha(colors.textPrimary, 0.04) },
        ticks: { color: colors.textMuted, precision: 0 },
      },
    },
  };

  if (error) {
    return (
      <section ref={ref}>
        <div className="mb-4">
          <SectionLabel>Performance Metrics</SectionLabel>
        </div>
        <Card>
          <SectionError message={error} onRetry={retry} />
        </Card>
      </section>
    );
  }

  const ready = !loading && !!data;
  const hasTeams = ready && data.topTeams.values.length > 0;
  const performers = ready ? data.topPerformers : [];

  return (
    <section ref={ref}>
      <div className="mb-4">
        <SectionLabel>Performance Metrics</SectionLabel>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-stretch">
        {/* Top teams */}
        <Card className="p-5.5">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h3 className="text-[13.5px] font-semibold text-(--color-text-primary)">
                Top Teams by Played Boxes
              </h3>
              <p className="text-xs text-(--color-text-muted) mt-0.5">
                Box played by teams
              </p>
            </div>
          </div>
          <div className="h-75 relative">
            {!ready ? (
              <div className="h-full w-full rounded-lg bg-white/5 animate-pulse" />
            ) : hasTeams ? (
              <Bar
                data={teamsData}
                options={teamsOptions}
                plugins={[barGlowPlugin]}
              />
            ) : (
              <div className="h-full flex items-center justify-center text-[13px] text-(--color-text-muted) text-center">
                No team play activity yet.
              </div>
            )}
          </div>
        </Card>

        {/* Top performers table */}
        <Card className="p-0 flex flex-col">
          <div className="flex justify-between items-center px-5 py-4 border-b border-(--color-border)">
            <h3 className="text-[13.5px] font-semibold text-(--color-text-primary)">
              Top Performers
            </h3>
          </div>
          {!ready ? (
            <div className="p-5 flex flex-col gap-3">
              {[0, 1, 2, 3, 4].map((i) => (
                <div key={i} className="h-9 rounded bg-white/5 animate-pulse" />
              ))}
            </div>
          ) : performers.length === 0 ? (
            <div className="px-5 py-8 text-[13px] text-(--color-text-muted) text-center">
              No performer data yet.
            </div>
          ) : (
            <div className="overflow-x-auto flex-1">
              <table className="w-full text-sm">
                <thead>
                  <tr>
                    {["Member", "Team", "Attempts", "Completed"].map((h) => (
                      <th
                        key={h}
                        className="text-[11.5px] font-bold text-(--color-text-primary) px-5 py-2.5 text-left"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {performers.map((p) => (
                    <tr
                      key={p.id}
                      className="hover:bg-white/2.5 transition-colors"
                    >
                      <td className="px-5 py-2.5 border-t border-(--color-border)">
                        <div className="flex items-center gap-2.25">
                          <Avatar initials={p.initials} tint={p.tint} />
                          <span className="text-(--color-text-primary) font-medium">
                            {p.name}
                          </span>
                        </div>
                      </td>
                      <td className="px-5 py-2.5 border-t border-(--color-border) text-(--color-text-secondary)">
                        {p.team}
                      </td>
                      <td className="px-5 py-2.5 border-t border-(--color-border)">
                        <span className="text-(--color-primary) font-semibold">
                          {p.attempts}
                        </span>
                      </td>
                      <td className="px-5 py-2.5 border-t border-(--color-border)">
                        <span className="font-semibold text-(--color-text-primary)">
                          {p.completed}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   Section: Learning Activity (4 stat cards)
   Wired to /api/v1/dashboard/company-admin/learning-activity
   ───────────────────────────────────────────── */
function LearningActivity() {
  const [ref, inView] = useInView(0.1);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reload, setReload] = useState(0);
  const retry = () => setReload((n) => n + 1);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const raw = await getLearningActivity();
        if (!cancelled) setData(normalizeLearningActivity(raw));
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reload]);

  const run = inView && !!data;
  const totalBoxes = useCountUp(data?.totalBoxes ?? 0, run);
  const completed = useCountUp(data?.completed ?? 0, run);
  const attempted = useCountUp(data?.attempted ?? 0, run);
  const activeRuns = useCountUp(data?.activeRuns ?? 0, run);

  const [expanded, setExpanded] = useState(false);
  // Once opened, the panel stays mounted and is only hidden on collapse —
  // re-mounting it would re-request the catalog on every toggle.
  const [hasOpened, setHasOpened] = useState(false);

  const toggleExpanded = () => {
    setHasOpened(true);
    setExpanded((v) => !v);
  };

  if (error) {
    return (
      <section ref={ref}>
        <div className="mb-4">
          <SectionLabel>Learning Activity</SectionLabel>
        </div>
        <Card>
          <SectionError message={error} onRetry={retry} />
        </Card>
      </section>
    );
  }

  if (loading || !data) {
    return (
      <section ref={ref}>
        <div className="mb-4">
          <SectionLabel>Learning Activity</SectionLabel>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4.5">
          {[0, 1, 2, 3].map((i) => (
            <Card key={i} className="p-5">
              <div className="animate-pulse flex flex-col gap-3">
                <div className="flex items-center gap-2.75">
                  <div className="w-10 h-10 rounded-full bg-white/5" />
                  <div className="h-3 w-24 rounded bg-white/5" />
                </div>
                <div className="h-12 rounded bg-white/5" />
              </div>
            </Card>
          ))}
        </div>
      </section>
    );
  }

  return (
    <section ref={ref}>
      <div className="mb-4">
        <SectionLabel>Learning Activity</SectionLabel>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4.5">
        <Card
          className="p-5 qv-ping cursor-pointer"
          style={{ "--ping-x": "50%", "--ping-y": "50%" }}
          role="button"
          tabIndex={0}
          aria-expanded={expanded}
          onClick={toggleExpanded}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              toggleExpanded();
            }
          }}
        >
          <div className="flex items-center gap-2.75 mb-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center bg-(--color-primary)/15">
              <Package size={18} className="text-(--color-primary)" />
            </div>
            <span className="text-[13px] text-(--color-text-primary)">
              Total Boxes
            </span>
          </div>
          <div className="qv-num text-[32px] mb-1.5 text-(--color-text-primary)">
            {totalBoxes}
          </div>
          <div className="text-[12.5px] text-(--color-text-muted) flex items-center justify-between gap-2">
            in the box library
            <span className="flex items-center gap-1.5 text-[11px] font-semibold">
              {expanded ? "Hide boxes" : "View boxes"}
              <ChevronDown
                size={14}
                className={`transition-transform duration-300 ${expanded ? "rotate-180" : ""}`}
              />
            </span>
          </div>
        </Card>

        <Card className="p-5">
          <div className="flex items-center gap-2.75 mb-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center bg-blue-500/12">
              <CheckCircle size={18} className="text-blue-400" />
            </div>
            <span className="text-[13px] text-(--color-text-primary)">
              Boxes Completed
            </span>
          </div>
          <div className="flex items-baseline gap-2">
            <div className="qv-num text-[32px] text-(--color-text-primary)">
              {completed}
            </div>
            <span className="text-[13px] text-(--color-text-muted) font-medium">
              by members
            </span>
          </div>
          <div className="text-[12.5px] text-(--color-text-muted) mt-1.5 flex items-center gap-1.5">
            <Package size={14} className="text-blue-400" />
            from a library of{" "}
            <span className="text-(--color-text-secondary) font-semibold">
              {data.totalBoxes}
            </span>{" "}
            unique boxes
          </div>
        </Card>

        <Card className="p-5">
          <div className="flex items-center gap-2.75 mb-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center bg-orange-500/12">
              <Boxes size={18} className="text-orange-400" />
            </div>
            <span className="text-[13px] text-(--color-text-primary)">
              Boxes Attempted
            </span>
          </div>
          <div className="flex items-baseline gap-2">
            <div className="qv-num text-[32px] text-(--color-text-primary)">
              {attempted}
            </div>
            <span className="text-[13px] text-(--color-text-muted) font-medium">
              by members
            </span>
          </div>
          <div className="text-[12.5px] text-(--color-text-muted) mt-1.5 flex items-center gap-1.5">
            <Package size={14} className="text-blue-400" />
            from a library of{" "}
            <span className="text-(--color-text-secondary) font-semibold">
              {data.totalBoxes}
            </span>{" "}
            unique boxes
          </div>
        </Card>

        <Card className="p-5">
          <div className="flex items-center gap-2.75 mb-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center bg-(--color-primary)/15">
              <Zap size={18} className="text-(--color-primary)" />
            </div>
            <span className="text-[13px] text-(--color-text-primary)">
              Active Runs
            </span>
          </div>
          <div className="flex items-baseline gap-2">
            <div className="qv-num text-[32px] text-(--color-primary)">
              {activeRuns}
            </div>
            <span className="text-[13px] text-(--color-text-muted) font-medium">
              boxes running now
            </span>
          </div>
          <div className="text-xs text-(--color-text-muted) mt-1.5 flex items-center gap-1.5">
            <span className="flex items-center gap-1">
              <User size={13} className="text-(--color-primary)" />
              <span className="text-(--color-text-secondary) font-semibold">
                {data.runsIndividual}
              </span>
              individual
            </span>
            <span className="text-(--color-text-muted)">+</span>
            <span className="flex items-center gap-1">
              <UsersIcon size={13} className="text-blue-400" />
              <span className="text-(--color-text-secondary) font-semibold">
                {data.runsTeam}
              </span>
              team
            </span>
          </div>
        </Card>
      </div>

      {hasOpened && (
        <div className={expanded ? "" : "hidden"}>
          <BoxLinksPanel />
        </div>
      )}
    </section>
  );
}

/* ─────────────────────────────────────────────
   Section: Box Library & Box Creation
   Wired to /api/v1/dashboard/company-admin/box-library
   ───────────────────────────────────────────── */
function BoxLibrarySection({ colors }) {
  const [ref, inView] = useInView(0.05);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reload, setReload] = useState(0);
  const retry = () => setReload((n) => n + 1);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const raw = await getBoxLibrary(5, 8);
        if (!cancelled) setData(normalizeBoxLibrary(raw));
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reload]);

  const run = inView && !!data;
  const successful = useCountUp(data?.outcomes.successful ?? 0, run);
  const failed = useCountUp(data?.outcomes.failed ?? 0, run);

  const weeklyData = useMemo(() => {
    if (!data) return null;
    return {
      labels: data.weekly.labels,
      datasets: [
        {
          data: data.weekly.values,
          borderRadius: 4,
          borderSkipped: false,
          backgroundColor: (ctx) => {
            const { ctx: c, chartArea } = ctx.chart;
            if (!chartArea) return withAlpha(colors.primary, 0.4);
            const g = c.createLinearGradient(0, 0, 0, 200);
            g.addColorStop(0, withAlpha(colors.primary, 0.65));
            g.addColorStop(1, withAlpha(colors.primary, 0.2));
            return g;
          },
        },
      ],
    };
  }, [data, colors]);

  const weeklyOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        ...tipPreset(colors),
        callbacks: { label: (ctx) => ` ${ctx.parsed.y} runs` },
      },
    },
    scales: {
      x: { grid: { display: false }, ticks: { color: colors.textMuted } },
      y: {
        beginAtZero: true,
        grid: { color: withAlpha(colors.textPrimary, 0.04) },
        ticks: { color: colors.textMuted, precision: 0 },
      },
    },
  };

  const tile = (label, value, color) => (
    <div className="rounded-[10px] p-3 bg-(--color-bg)/60">
      <div className="text-[10px] uppercase tracking-widest text-(--color-text-muted) font-mono font-semibold mb-1.5">
        {label}
      </div>
      <div
        className="qv-num text-[24px]"
        style={{ color: color || "var(--color-text-primary)" }}
      >
        {value}
      </div>
    </div>
  );

  const FunnelRow = ({ label, value, pct, color, delay }) => (
    <div className="flex items-center gap-2.5">
      <span className="text-xs text-(--color-text-secondary) w-22 shrink-0">
        {label}
      </span>
      <div className="flex-1 h-1.5 rounded-[3px] overflow-hidden bg-(--color-border)/50">
        <div
          className="h-full rounded-[3px] transition-[width] duration-700 ease-out"
          style={{
            width: run ? `${pct}%` : 0,
            background: color,
            transitionDelay: delay,
          }}
        />
      </div>
      <span className="text-xs font-semibold w-9 text-right text-(--color-text-primary)">
        {value}
      </span>
    </div>
  );

  if (error) {
    return (
      <section ref={ref}>
        <div className="mb-4">
          <SectionLabel>Box Library &amp; Box Creation</SectionLabel>
        </div>
        <Card>
          <SectionError message={error} onRetry={retry} />
        </Card>
      </section>
    );
  }

  const ready = !loading && !!data;
  const hasWeekly = ready && data.weekly.values.length > 0;

  return (
    <section ref={ref}>
      <div className="mb-4">
        <SectionLabel>Box Library &amp; Box Creation</SectionLabel>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Org Box Library */}
        <Card className="p-5.5">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-[13.5px] font-semibold text-(--color-text-primary)">
              Org Box Library
            </h3>
            <button
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-[10px] text-xs font-semibold text-white"
              style={{
                background:
                  "linear-gradient(180deg, var(--color-primary), var(--color-primary-hover))",
              }}
            >
              <Plus size={15} /> New Box
            </button>
          </div>

          {!ready ? (
            <div className="grid grid-cols-2 gap-2.5 mb-4">
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="h-16 rounded-[10px] bg-white/5 animate-pulse"
                />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2.5 mb-4">
              {tile("Owned", data.owned)}
              {tile("Pending", data.pending, "#f59e0b")}
              {tile(
                "Approved This Month",
                data.approvedThisMonth,
                "var(--color-primary)",
              )}
              {tile("Approved by Me", data.approvedByMe)}
            </div>
          )}

          <div className="text-xs font-semibold mb-2.5 text-(--color-text-secondary)">
            Top Creators Within Org
          </div>
          {!ready ? (
            <div className="flex flex-col gap-2">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-6 rounded bg-white/5 animate-pulse" />
              ))}
            </div>
          ) : data.topCreators.length === 0 ? (
            <div className="text-[12.5px] text-(--color-text-muted) py-2">
              No creators yet.
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {data.topCreators.map((c) => (
                <div
                  key={c.id}
                  className="flex items-center gap-2.5 text-[12.5px]"
                >
                  <Avatar initials={c.initials} tint={c.tint} />
                  <span className="text-(--color-text-secondary) flex-1">
                    {c.name}
                  </span>
                  <span className="text-(--color-primary) font-semibold">
                    {c.boxes} {c.boxes === 1 ? "Box" : "Boxes"}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Box Creation */}
        <Card className="p-5.5">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h3 className="text-[13.5px] font-semibold text-(--color-text-primary)">
                Box Creation
              </h3>
              <p className="text-xs text-(--color-text-muted) mt-0.5">
                Boxes created by org members
              </p>
            </div>
            <div className="text-right">
              <div className="qv-num text-[20px] text-(--color-primary)">
                {ready ? `${data.outcomes.successRate}%` : "—"}
              </div>
              <div className="text-[10.5px] text-(--color-text-muted) font-mono">
                SUCCESS RATE
              </div>
            </div>
          </div>

          <div className="mb-5">
            <div className="text-[11.5px] font-semibold text-(--color-text-secondary) mb-2">
              Weekly Run Volume
            </div>
            <div className="h-50">
              {!ready ? (
                <div className="h-full w-full rounded-lg bg-white/5 animate-pulse" />
              ) : hasWeekly ? (
                <Bar
                  data={weeklyData}
                  options={weeklyOptions}
                  plugins={[barGlowPlugin]}
                />
              ) : (
                <div className="h-full flex items-center justify-center text-[13px] text-(--color-text-muted) text-center">
                  No runs in this period.
                </div>
              )}
            </div>
          </div>

          <div className="text-[11.5px] font-semibold text-(--color-text-secondary) mb-2.5">
            Run Outcomes
          </div>
          {!ready ? (
            <div className="flex flex-col gap-2.5">
              <div className="h-5 rounded bg-white/5 animate-pulse" />
              <div className="h-5 rounded bg-white/5 animate-pulse" />
            </div>
          ) : (
            <div className="flex flex-col gap-2.5">
              <FunnelRow
                label="Successful"
                value={successful}
                pct={data.outcomes.successPct}
                color={colors.primary}
                delay="0s"
              />
              <FunnelRow
                label="Failed"
                value={failed}
                pct={data.outcomes.failedPct}
                color={colors.red}
                delay="0.15s"
              />
            </div>
          )}
        </Card>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   Section: LLM Token Usage
   Wired to /api/v1/dashboard/company-admin/token-usage
   ───────────────────────────────────────────── */
function TokenUsage({ colors }) {
  const [ref] = useInView(0.05);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reload, setReload] = useState(0);
  const retry = () => setReload((n) => n + 1);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const raw = await getTokenUsage(5);
        if (!cancelled) setData(normalizeTokenUsage(raw));
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reload]);

  const tokenData = useMemo(() => {
    if (!data) return null;
    return {
      labels: data.labels,
      datasets: [
        {
          data: data.series,
          fill: true,
          borderColor: colors.orange,
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 4,
          pointHoverBackgroundColor: colors.orange,
          tension: 0.4,
          backgroundColor: (ctx) => {
            const { ctx: c, chartArea } = ctx.chart;
            if (!chartArea) return withAlpha(colors.orange, 0.1);
            const g = c.createLinearGradient(0, 0, 0, 175);
            g.addColorStop(0, withAlpha(colors.orange, 0.22));
            g.addColorStop(1, withAlpha(colors.orange, 0));
            return g;
          },
        },
      ],
    };
  }, [data, colors]);

  const tokenOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: {
        ...tipPreset(colors),
        callbacks: {
          label: (ctx) => {
            const k =
              ctx.parsed.y >= 1000
                ? (ctx.parsed.y / 1000).toFixed(1) + "K"
                : ctx.parsed.y;
            return ` ${k} tokens`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { maxTicksLimit: 6, color: colors.textMuted },
      },
      y: {
        beginAtZero: true,
        grid: { color: withAlpha(colors.textPrimary, 0.04) },
        ticks: {
          color: colors.textMuted,
          callback: (v) => (v >= 1000 ? (v / 1000).toFixed(0) + "K" : v),
        },
      },
    },
  };

  const statTile = (value, label) => (
    <div className="rounded-lg p-2.5 text-center bg-(--color-bg)/60">
      <div className="qv-num text-[17px] text-(--color-text-primary)">
        {value}
      </div>
      <div className="text-[10px] text-(--color-text-muted) mt-0.5">
        {label}
      </div>
    </div>
  );

  if (error) {
    return (
      <section ref={ref}>
        <div className="mb-4">
          <SectionLabel>LLM Token Usage</SectionLabel>
        </div>
        <Card>
          <SectionError message={error} onRetry={retry} />
        </Card>
      </section>
    );
  }

  const ready = !loading && !!data;
  const hasSeries = ready && data.series.some((v) => v > 0);

  return (
    <section ref={ref}>
      <div className="mb-4">
        <SectionLabel>LLM Token Usage</SectionLabel>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Token chart */}
        <Card className="p-5.5 flex flex-col lg:col-span-2">
          <div className="flex justify-between items-start mb-1.5">
            <div>
              <h3 className="text-[13.5px] font-semibold text-(--color-text-primary)">
                Token Consumption — 30 Day Trend
              </h3>
              <p className="text-xs text-(--color-text-muted) mt-0.5">
                Daily Lab Assistant tokens used by org members
              </p>
            </div>
            <div className="text-right">
              <div className="qv-num text-[20px] text-orange-500">
                {ready ? data.stats.total : "—"}
              </div>
              <div className="text-[10.5px] text-(--color-text-muted) font-mono">
                TOTAL (30D)
              </div>
            </div>
          </div>
          <div className="h-43.75 mt-2.5">
            {!ready ? (
              <div className="h-full w-full rounded-lg bg-white/5 animate-pulse" />
            ) : hasSeries ? (
              <Line
                data={tokenData}
                options={tokenOptions}
                plugins={[lineGlowPlugin]}
              />
            ) : (
              <div className="h-full flex items-center justify-center text-[13px] text-(--color-text-muted) text-center">
                No token usage in the last 30 days.
              </div>
            )}
          </div>
          <div className="grid grid-cols-3 gap-2.5 mt-3.5">
            {statTile(ready ? data.stats.today : "—", "Today")}
            {statTile(ready ? data.stats.week : "—", "This Week")}
            {statTile(ready ? data.stats.avgTurn : "—", "Avg/Turn")}
          </div>
        </Card>

        {/* At-risk + top consumers */}
        <Card
          className="p-5 qv-ping flex flex-col gap-5"
          style={{ "--ping-x": "50%", "--ping-y": "50%" }}
        >
          <div>
            <div className="flex items-center gap-1.5 mb-3">
              <AlertTriangle size={18} className="text-red-500" />
              <h3 className="text-[13px] font-semibold text-(--color-text-primary)">
                Near 50K Cap
              </h3>
              {ready && data.nearCap.length > 0 && (
                <span className="ml-auto inline-flex items-center px-2.25 py-0.75 rounded-md text-[11px] font-semibold bg-red-500/12 text-red-500 border border-red-500/25">
                  {data.nearCap.length}{" "}
                  {data.nearCap.length === 1 ? "member" : "members"}
                </span>
              )}
            </div>
            {!ready ? (
              <div className="flex flex-col gap-2">
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    className="h-8 rounded bg-white/5 animate-pulse"
                  />
                ))}
              </div>
            ) : data.nearCap.length === 0 ? (
              <div className="text-[12.5px] text-(--color-text-muted) py-1">
                No members near the cap.
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                {data.nearCap.map((m) => {
                  const col = m.pct >= 96 ? colors.red : colors.orange;
                  return (
                    <div key={m.id} className="flex flex-col gap-1.5">
                      <div className="flex items-center gap-2 text-[12.5px]">
                        <Avatar initials={m.initials} tint={m.tint} />
                        <span className="text-(--color-text-secondary) flex-1">
                          {m.name}
                        </span>
                        <span className="font-semibold" style={{ color: col }}>
                          {(m.tokens / 1000).toFixed(1)}K
                        </span>
                      </div>
                      <div className="h-1.25 rounded-[3px] overflow-hidden bg-(--color-border)/50">
                        <div
                          className="h-full rounded-[3px]"
                          style={{ width: `${m.pct}%`, background: col }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="border-t border-(--color-border) pt-4">
            <h3 className="text-[13px] font-semibold mb-3 text-(--color-text-primary)">
              Top Consumers
            </h3>
            {!ready ? (
              <div className="flex flex-col gap-2">
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    className="h-6 rounded bg-white/5 animate-pulse"
                  />
                ))}
              </div>
            ) : data.topConsumers.length === 0 ? (
              <div className="text-[12.5px] text-(--color-text-muted) py-1">
                No consumer data yet.
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                {data.topConsumers.map((c) => (
                  <div
                    key={c.id}
                    className="flex items-center gap-2.25 text-[12.5px]"
                  >
                    <Avatar initials={c.initials} tint={c.tint} />
                    <span className="text-(--color-text-secondary) flex-1">
                      {c.name}
                    </span>
                    <span className="font-semibold text-(--color-text-primary)">
                      {c.value}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   Page
   ───────────────────────────────────────────── */
export default function CompanyAdminDashboard() {
  const colors = useThemeColors();
  const { user } = useAuth();
  const displayName = user?.name || user?.username || "Admin";

  return (
    <CompanyAdminLayout>
      <div className="px-8 py-8 space-y-7">
        {/* Header */}
        <div>
          <div className="text-[10.5px] tracking-[0.22em] uppercase text-(--color-text-muted) font-mono">
            Company Overview
          </div>
          <h1 className="qv-serif mt-2 text-[40px] leading-[1.05] font-medium tracking-tight text-(--color-text-primary)">
            Welcome back,{" "}
            <span className="text-(--color-primary)">{displayName}</span>.
          </h1>
          <p className="mt-2 text-sm text-(--color-text-secondary)">
            Your organization's activity, members, and performance at a glance.
          </p>
        </div>

        {colors && (
          <>
            <OrgOverview colors={colors} />
            <MemberAnalytics colors={colors} />
            <PerformanceMetrics colors={colors} />
            <LearningActivity />
            <BoxLibrarySection colors={colors} />
            <TokenUsage colors={colors} />
          </>
        )}
      </div>
    </CompanyAdminLayout>
  );
}
